import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Download, ExternalLink, Loader2, ArrowUp, ArrowDown, FolderOpen } from 'lucide-react';
import type { FaturaPainel } from '../types';
import type { NomesDaUc } from '../../../../src/lib/financeiro/nomesDaUc';
import { valorDevido } from '../../../../src/lib/statusBoleto';
import { mesReferenciaParaData } from '../../../../src/lib/financeiro/periodo';
import { dataVencimentoDe, diasPagamentoAposVencimento, venceEmAte7Dias, somaValorDevido } from '../../../../src/lib/financeiro/definicoesKpi';
import { propsFundoModal } from '../../../../src/lib/backdropModal';
import { CORES as CORES_STATUS } from './BoletosPorStatusDonut';

interface Props {
  aberto: boolean;
  titulo: string;
  descricao: string;
  faturas: FaturaPainel[]; // já recortadas pelo KPI — o total delas é o do card
  hoje: Date;
  mostrarDias?: boolean; // prazo médio / recuperado: coluna "dias após vencimento"
  permitirFiltro7Dias?: boolean; // A Vencer: atalho "só as que vencem em 7 dias"
  /** Enquanto o modal da UC estiver aberto por cima, o Esc é dele. */
  escAtivo: boolean;
  abrindoUcId: string | null;
  abrindoBoletoId: string | null;
  exportando: boolean;
  onFechar: () => void;
  onAbrirUc: (f: FaturaPainel) => void;
  onAbrirBoleto: (f: FaturaPainel) => void;
  onExportar: (faturasVisiveis: FaturaPainel[]) => void;
  /** 'lateral' (Painel Financeiro) desliza da direita; 'modal' (Painel Executivo
   *  geral) abre centralizado. A tabela, a busca e o Excel são os mesmos. */
  layout?: 'lateral' | 'modal';
  /** Informada, mostra a coluna "Parte da Vigor" e o total dela — ver `parteVigorDaFatura`. */
  parteVigor?: (f: FaturaPainel) => number;
  /** Assinante e titular da conta de luz — papéis diferentes, ver `nomesDaUc.ts`. */
  nomes: (f: FaturaPainel) => NomesDaUc;
}

type Coluna = 'cliente' | 'titular' | 'uc' | 'mes' | 'vencimento' | 'pagamento' | 'dias' | 'valor' | 'vigor' | 'status';

const TAMANHO_PAGINA = 100;

const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });
const fmtData = (v: string | null | undefined) => {
  if (!v) return '—';
  const [a, m, d] = v.split('T')[0].split('-');
  return a && m && d ? `${d}/${m}/${a}` : '—';
};

function normalizar(texto: string | null | undefined): string {
  return (texto || '').normalize('NFD').replace(/\p{Mn}/gu, '').toLowerCase();
}

const temBoleto = (f: FaturaPainel) => !!(f.url_boleto || f.url_boleto_combinado || f.pdf_boleto_combinado);

function valorOrdenacao(
  f: FaturaPainel,
  coluna: Coluna,
  nomes: (f: FaturaPainel) => NomesDaUc,
  parteVigor?: (f: FaturaPainel) => number,
): string | number {
  switch (coluna) {
    case 'vigor': return parteVigor ? parteVigor(f) : 0;
    case 'cliente': return normalizar(nomes(f).assinante);
    case 'titular': return normalizar(nomes(f).titular);
    case 'uc': return f.uc_codigo || '';
    case 'mes': return mesReferenciaParaData(f.mes_referencia)?.getTime() ?? 0;
    case 'vencimento': return dataVencimentoDe(f) || '';
    case 'pagamento': return f.data_pgto_boleto || '';
    case 'dias': return diasPagamentoAposVencimento(f) ?? Number.NEGATIVE_INFINITY;
    case 'valor': return valorDevido(f);
    case 'status': return f.statusDerivado;
  }
}

const ListaBoletosDrawer: React.FC<Props> = ({
  aberto, titulo, descricao, faturas, hoje, mostrarDias = false, permitirFiltro7Dias = false,
  escAtivo, abrindoUcId, abrindoBoletoId, exportando, onFechar, onAbrirUc, onAbrirBoleto, onExportar,
  layout = 'lateral', parteVigor, nomes,
}) => {
  const [busca, setBusca] = useState('');
  const [so7Dias, setSo7Dias] = useState(false);
  const [ordem, setOrdem] = useState<{ coluna: Coluna; desc: boolean }>({ coluna: 'valor', desc: true });
  const [limite, setLimite] = useState(TAMANHO_PAGINA);
  const buscaRef = useRef<HTMLInputElement>(null);
  const focoAnterior = useRef<HTMLElement | null>(null);

  // Cada card abre a lista "limpa": busca, atalho e paginação não vazam de um card para outro.
  useEffect(() => {
    if (!aberto) return;
    setBusca('');
    setSo7Dias(false);
    setOrdem({ coluna: 'valor', desc: true });
    setLimite(TAMANHO_PAGINA);
  }, [aberto, titulo]);

  // Mesmo padrão de foco de JustificativaAtrasoModal/ModalBusca: ao abrir, foco vai
  // pro primeiro campo interativo; ao fechar, devolve pro elemento que abriu o drawer
  // (o card do KPI) — sem isso quem navega por teclado/leitor de tela perde a posição.
  useEffect(() => {
    if (aberto) {
      focoAnterior.current = document.activeElement as HTMLElement | null;
      const t = setTimeout(() => buscaRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
    focoAnterior.current?.focus();
  }, [aberto]);

  useEffect(() => {
    if (!aberto || !escAtivo) return;
    const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') onFechar(); };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [aberto, escAtivo, onFechar]);

  const visiveis = useMemo(() => {
    const termo = normalizar(busca.trim());
    const termoDigitos = busca.replace(/\D/g, '');
    const filtradas = faturas.filter((f) => {
      if (so7Dias && !venceEmAte7Dias(f, hoje)) return false;
      if (!termo) return true;
      // A busca varre os DOIS nomes: quem procura pelo titular da conta de luz
      // não acha nada se só o assinante for pesquisável.
      const { assinante, titular } = nomes(f);
      return normalizar(assinante).includes(termo)
        || normalizar(titular).includes(termo)
        || normalizar(f.cliente_nome).includes(termo)
        || (termoDigitos.length >= 3 && ((f.uc_codigo || '').replace(/\D/g, '').includes(termoDigitos)
          || (f.cliente_cpf_cnpj || '').replace(/\D/g, '').includes(termoDigitos)));
    });
    const sinal = ordem.desc ? -1 : 1;
    return filtradas.sort((a, b) => {
      const va = valorOrdenacao(a, ordem.coluna, nomes, parteVigor);
      const vb = valorOrdenacao(b, ordem.coluna, nomes, parteVigor);
      return va < vb ? -sinal : va > vb ? sinal : 0;
    });
  }, [faturas, busca, so7Dias, ordem, hoje, parteVigor, nomes]);

  if (!aberto) return null;

  const totalCard = somaValorDevido(faturas);
  const recortado = visiveis.length !== faturas.length;
  const totalVisiveis = recortado ? somaValorDevido(visiveis) : totalCard;
  const somaVigor = (lista: FaturaPainel[]) => (parteVigor ? lista.reduce((s, f) => s + parteVigor(f), 0) : 0);
  const vigorCard = somaVigor(faturas);
  const vigorVisiveis = recortado ? somaVigor(visiveis) : vigorCard;
  const modal = layout === 'modal';

  const alternarOrdem = (coluna: Coluna) =>
    setOrdem((o) => (o.coluna === coluna ? { coluna, desc: !o.desc } : { coluna, desc: coluna === 'valor' || coluna === 'vigor' || coluna === 'dias' }));

  // Função e não componente: declarado aqui dentro, um componente remontaria a
  // cada render e o botão perderia o foco logo depois do clique.
  const cabecalho = (coluna: Coluna, children: React.ReactNode, alinhar: 'left' | 'right' = 'left') => {
    const ativa = ordem.coluna === coluna;
    return (
      <th key={coluna} className={`px-3 py-2 font-medium whitespace-nowrap ${alinhar === 'right' ? 'text-right' : 'text-left'}`} aria-sort={ativa ? (ordem.desc ? 'descending' : 'ascending') : 'none'}>
        <button type="button" onClick={() => alternarOrdem(coluna)} className={`inline-flex items-center gap-1 hover:text-gray-800 dark:hover:text-gray-200 ${ativa ? 'text-gray-800 dark:text-gray-100' : ''}`}>
          {children}
          {ativa && (ordem.desc ? <ArrowDown size={12} /> : <ArrowUp size={12} />)}
        </button>
      </th>
    );
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-[90] flex bg-black/40 backdrop-blur-sm animate-fade-in ${modal ? 'items-center justify-center p-2 sm:p-6' : 'justify-end'}`}
      {...propsFundoModal(onFechar)}
    >
      {/* Largo: são 10 colunas, com dois nomes de pessoa e três valores. A
          lateral cresceu junto — a tabela é a mesma nas duas telas. */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="lista-boletos-titulo"
        className={`w-full max-w-[1800px] bg-white dark:bg-gray-900 shadow-2xl flex flex-col ${modal ? 'max-h-full h-[92vh] rounded-2xl overflow-hidden' : 'h-full'}`}
      >
        <header className="px-5 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 id="lista-boletos-titulo" className="text-lg font-poppins font-bold text-gray-900 dark:text-white">
                {titulo}
              </h2>
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mt-0.5">
                {faturas.length.toLocaleString('pt-BR')} boletos · {fmtBRL(totalCard)}
                {parteVigor && (
                  <span className="font-normal text-gray-500 dark:text-gray-400"> · parte da Vigor {fmtBRL(vigorCard)}</span>
                )}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{descricao}</p>
            </div>
            <button type="button" onClick={onFechar} aria-label="Fechar lista de boletos" className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-800">
              <X size={18} />
            </button>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-4">
            <div className="relative flex-1 sm:max-w-sm">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                ref={buscaRef}
                type="text"
                value={busca}
                onChange={(e) => { setBusca(e.target.value); setLimite(TAMANHO_PAGINA); }}
                placeholder="Buscar cliente, UC ou CPF/CNPJ..."
                aria-label="Buscar na lista de boletos"
                className="w-full pl-9 pr-3 py-2 text-sm rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            {permitirFiltro7Dias && (
              <label className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer select-none">
                <input type="checkbox" checked={so7Dias} onChange={(e) => { setSo7Dias(e.target.checked); setLimite(TAMANHO_PAGINA); }} className="rounded border-gray-300 text-primary focus:ring-primary/40" />
                Só as que vencem em 7 dias
              </label>
            )}
            <button
              type="button"
              onClick={() => onExportar(visiveis)}
              disabled={exportando || visiveis.length === 0}
              className="sm:ml-auto inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-primary text-white hover:bg-primary-dark disabled:opacity-50 transition-colors"
            >
              {exportando ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
              Exportar esta lista
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-auto">
          {visiveis.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-16">
              {faturas.length === 0 ? 'Nenhum boleto neste indicador com os filtros atuais.' : 'Nenhum boleto encontrado para esta busca.'}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-xs border-b border-gray-100 dark:border-gray-700">
                <tr>
                  {cabecalho('cliente', 'Assinante')}
                  {cabecalho('titular', 'Titular da fatura')}
                  {cabecalho('uc', 'UC')}
                  {cabecalho('mes', 'Mês ref.')}
                  {cabecalho('vencimento', 'Vencimento')}
                  {cabecalho('pagamento', 'Pagamento')}
                  {mostrarDias && cabecalho('dias', 'Dias', 'right')}
                  {cabecalho('valor', 'Valor', 'right')}
                  {parteVigor && cabecalho('vigor', 'Parte da Vigor', 'right')}
                  {cabecalho('status', 'Status')}
                  <th className="px-3 py-2 text-right font-medium">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                {visiveis.slice(0, limite).map((f) => {
                  const dias = diasPagamentoAposVencimento(f);
                  const abrindoUc = abrindoUcId === f.id;
                  const abrindoBoleto = abrindoBoletoId === f.id;
                  // Assinante e titular são papéis distintos e aparecem sempre os
                  // dois: o boleto sai no nome do assinante, e a conta de luz pode
                  // estar no nome de outra pessoa — ver `nomesDaUc.ts`.
                  const { assinante, titular } = nomes(f);
                  return (
                    <tr key={f.id} className="hover:bg-gray-50/70 dark:hover:bg-gray-800/50">
                      <td className="px-3 py-2.5 max-w-[300px]">
                        <p className="font-medium text-gray-800 dark:text-gray-100 truncate" title={assinante}>{assinante}</p>
                        <p className="text-[11px] text-gray-400 truncate">
                          {[f.cliente_cpf_cnpj, f.unidade_geradora_1].filter(Boolean).join(' · ') || '—'}
                        </p>
                      </td>
                      <td className="px-3 py-2.5 max-w-[260px]">
                        {titular ? (
                          <p className={`truncate ${normalizar(titular) === normalizar(assinante) ? 'text-gray-500 dark:text-gray-400' : 'text-gray-800 dark:text-gray-100 font-medium'}`} title={titular}>
                            {titular}
                          </p>
                        ) : (
                          <span className="text-gray-400" title="O cadastro da UC não tem o titular da conta de luz">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300 whitespace-nowrap">{f.uc_codigo || '—'}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300 whitespace-nowrap">{f.mes_referencia || '—'}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300 whitespace-nowrap">{fmtData(dataVencimentoDe(f))}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300 whitespace-nowrap">{fmtData(f.data_pgto_boleto)}</td>
                      {mostrarDias && (
                        <td className={`px-3 py-2.5 text-right whitespace-nowrap ${dias !== null && dias > 0 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
                          {dias === null ? '—' : dias}
                        </td>
                      )}
                      <td className="px-3 py-2.5 text-right font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                        {fmtBRL(valorDevido(f))}
                        {Number(f.valor_emissao_boleto_unico) > 0 && <span className="block text-[10px] font-normal text-gray-400">boleto único</span>}
                      </td>
                      {parteVigor && (() => {
                        const vigor = parteVigor(f);
                        const menor = Math.abs(vigor - valorDevido(f)) >= 0.01;
                        return (
                          <td className={`px-3 py-2.5 text-right whitespace-nowrap ${menor ? 'font-semibold text-violet-700 dark:text-violet-300' : 'text-gray-600 dark:text-gray-300'}`}>
                            {fmtBRL(vigor)}
                            {menor && f.empresa_parceira_id && <span className="block text-[10px] font-normal text-gray-400">sem Equatorial e parceira</span>}
                          </td>
                        );
                      })()}
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: CORES_STATUS[f.statusDerivado] }} />
                          {f.statusDerivado}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-end gap-1.5">
                          {temBoleto(f) && (
                            <button
                              type="button"
                              onClick={() => onAbrirBoleto(f)}
                              disabled={abrindoBoleto}
                              title="Abrir o PDF do boleto"
                              aria-label={`Abrir boleto de ${f.cliente_nome || 'cliente'} ${f.mes_referencia || ''}`}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-primary hover:bg-primary/10 disabled:opacity-50"
                            >
                              {abrindoBoleto ? <Loader2 size={15} className="animate-spin" /> : <ExternalLink size={15} />}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => onAbrirUc(f)}
                            disabled={abrindoUc || !f.uc_codigo}
                            title={f.uc_codigo ? 'Abrir a UC com as ações' : 'Fatura sem número de UC'}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-primary/50 hover:text-primary disabled:opacity-50 disabled:hover:text-gray-600 whitespace-nowrap"
                          >
                            {abrindoUc ? <Loader2 size={13} className="animate-spin" /> : <FolderOpen size={13} />}
                            Abrir UC
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* md:pl-24: o botão flutuante da Vick (fixed bottom-5 left-5, 56px, só a
            partir de md) cai exatamente em cima do contador e do "Carregar mais",
            que ficam à esquerda deste rodapé. 96px livra o botão com folga. Abaixo
            de md o botão não aparece, então o padding original fica (5840). */}
        <footer className="px-5 md:pl-24 py-3 border-t border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
          <span className="text-gray-500 dark:text-gray-400">
            Mostrando {Math.min(limite, visiveis.length).toLocaleString('pt-BR')} de {visiveis.length.toLocaleString('pt-BR')}
          </span>
          {visiveis.length > limite && (
            <button type="button" onClick={() => setLimite((l) => l + TAMANHO_PAGINA)} className="text-primary font-semibold hover:underline sm:ml-2 text-left">
              Carregar mais {Math.min(TAMANHO_PAGINA, visiveis.length - limite)}
            </button>
          )}
          <span className="sm:ml-auto font-bold text-gray-900 dark:text-white">
            {recortado ? 'Total da busca' : 'Total'}: {fmtBRL(totalVisiveis)}
            {parteVigor && (
              <span className="font-semibold text-violet-700 dark:text-violet-300"> · Parte da Vigor: {fmtBRL(vigorVisiveis)}</span>
            )}
          </span>
        </footer>
      </aside>
    </div>,
    document.body
  );
};

export default ListaBoletosDrawer;
